import { useRef, useEffect } from "react";
import { useFormikContext } from "formik";
import isEqual from "lodash/isEqual";
import { useAutoSave } from "newui/hooks/useAutoSave";
import { useUnsavedChangesWarning } from "newui/hooks/useUnsavedChangesWarning";
import { useAutoSaveContext } from "./AutoSaveContext";

interface FormikAutoSaveProps {
	/** Debounce interval in milliseconds (default: 3000) */
	debounceMs?: number;
	/** External error flag — suppresses auto-save when true */
	hasError?: boolean;
	/** Warn user before navigating away with unsaved changes (default: true) */
	warnOnLeave?: boolean;
}

/**
 * Drop-in Formik auto-save component.
 *
 * Place inside a `<Formik>` render tree. Automatically submits the form
 * after `debounceMs` of inactivity when the form is dirty, valid, and
 * the values have changed since the last successful save.
 *
 * When wrapped in an `<AutoSaveProvider>`, it publishes its `isSaving`,
 * `lastSavedAt`, and `error` state so that `<AutoSaveIndicator />` placed
 * anywhere inside the same provider can display save status.
 *
 * Usage:
 * ```tsx
 * <AutoSaveProvider>
 *   <AutoSaveIndicator />
 *   <Formik ...>
 *     <Form>
 *       <FormikAutoSave debounceMs={3000} />
 *       ...fields...
 *     </Form>
 *   </Formik>
 * </AutoSaveProvider>
 * ```
 */
export const FormikAutoSave = <T extends Record<string, any>>({
	debounceMs = 3000,
	hasError = false,
	warnOnLeave = true,
}: FormikAutoSaveProps) => {
	const { dirty, isSubmitting, submitForm, validateForm, values } =
		useFormikContext<T>();

	// Warn user if they try to refresh/close with unsaved changes
	useUnsavedChangesWarning(warnOnLeave && dirty && !isSubmitting);

	const ctx = useAutoSaveContext();
	const ctxRef = useRef(ctx);
	ctxRef.current = ctx;
	const savedValuesRef = useRef<T>(values);
	const changeTokenRef = useRef(0);

	// When Formik re-initializes (e.g. after a manual save updates the cache),
	// dirty resets to false. Sync savedValuesRef so auto-save doesn't re-trigger.
	const prevDirtyRef = useRef(dirty);
	if (prevDirtyRef.current && !dirty) {
		savedValuesRef.current = values;
	}
	prevDirtyRef.current = dirty;

	const isDirty =
		dirty &&
		!hasError &&
		!isSubmitting &&
		!isEqual(savedValuesRef.current, values);

	// Increment token on every values change so the debounce timer resets
	const prevValuesRef = useRef(values);
	if (!isEqual(prevValuesRef.current, values)) {
		prevValuesRef.current = values;
		changeTokenRef.current += 1;
	}

	const { isSaving, lastSavedAt } = useAutoSave({
		isDirty,
		changeToken: changeTokenRef.current,
		onSave: async () => {
			const validationErrors = await validateForm();
			if (Object.keys(validationErrors).length > 0) {
				return false;
			}
			try {
				await submitForm();
				savedValuesRef.current = values;
				ctxRef.current?.setError(null);
			} catch (e) {
				ctxRef.current?.setError("Save failed");
				throw e;
			}
		},
		debounceMs,
	});

	// Sync state to context so AutoSaveIndicator can read it
	useEffect(() => {
		ctxRef.current?.setSaving(isSaving);
	}, [isSaving]);

	useEffect(() => {
		if (lastSavedAt) {
			ctxRef.current?.setLastSavedAt(lastSavedAt);
		}
	}, [lastSavedAt]);

	return null;
};
